import { DEFAULT_PAGING, type IOrder, type ListQueryVariables, type PagingInput } from './types';
import { getCurrentOrgId } from '@/store/use-my-oganization-store';

export type ResolvedListQueryVariables<TWhere = Record<string, unknown>> = {
  where: TWhere;
  order: IOrder;
  paging: PagingInput;
};

export const toListQueryVariables = <TWhere = Record<string, unknown>>(
  params?: ListQueryVariables<TWhere> | null
): ResolvedListQueryVariables<TWhere> => ({
  where: (params?.where ?? {}) as TWhere,
  order: params?.order ?? { direction: 'ASC', field: 'CreatedDate' },
  paging: {
    pageNo: params?.paging?.pageNo ?? DEFAULT_PAGING.pageNo,
    pageSize: params?.paging?.pageSize ?? DEFAULT_PAGING.pageSize,
  },
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Read `{ Field: { eq } }` from a where object, including a top-level `and` list. */
export const eqFromWhere = (where: unknown, field: string): string | null => {
  if (!isRecord(where)) return null;

  const node = where[field];
  if (isRecord(node) && 'eq' in node) {
    const eq = node.eq;
    return typeof eq === 'string' || eq == null ? (eq ?? null) : String(eq);
  }

  const and = where.and;
  if (Array.isArray(and)) {
    for (const clause of and) {
      const found = eqFromWhere(clause, field);
      if (found != null) return found;
    }
  }

  return null;
};

/** Prefer an explicit OrganizationId filter, otherwise the switched current org. */
export const organizationIdForQuery = (where: unknown): string | null =>
  eqFromWhere(where, 'OrganizationId') ??
  eqFromWhere(where, 'OrganizationIds') ??
  getCurrentOrgId();

export const toPagingOnlyVariables = <TWhere = Record<string, unknown>>(
  params?: ListQueryVariables<TWhere> | null
) => {
  const { paging } = toListQueryVariables(params);
  return {
    pageNo: paging.pageNo,
    pageSize: paging.pageSize,
  };
};

/** Live gateway list variables: scalars only (no FilterInput / SortInput / OffsetPaging vars). */
export const toScalarListVariables = <TWhere = Record<string, unknown>>(
  params: ListQueryVariables<TWhere> | null | undefined,
  mapping: Record<string, string>
) => {
  const { where, paging } = toListQueryVariables(params);
  const variables: Record<string, string | number | null> = {
    pageNo: paging.pageNo,
    pageSize: paging.pageSize,
  };
  for (const [varName, field] of Object.entries(mapping)) {
    variables[varName] = eqFromWhere(where, field);
  }
  if (
    Object.values(mapping).some((field) => field === 'OrganizationId' || field === 'OrganizationIds') &&
    variables.organizationId == null
  ) {
    variables.organizationId = getCurrentOrgId();
  }
  return variables;
};

/** @deprecated Use toScalarListVariables. Kept for callers still mapping OrganizationId. */
export const toGatewayListVariables = <TWhere = Record<string, unknown>>(
  params?: ListQueryVariables<TWhere> | null
) =>
  toScalarListVariables(params, {
    organizationId: 'OrganizationId',
  });
